// src/App.jsx
import React, { useState, useEffect } from "react";
import Login from "./Login";
import Dashboard from "./Dashboard";
import ClientDashboard from "./ClientDashboard";
import Settings from "./Settings";
import Clients from "./Clients";
import Podcasts from "./Podcasts";
import Episodes from "./Episodes";
import FileExplorer from "./FileExplorer";
import Leads from "./Leads";
import Layout from "./Layout";
import ReviewPage from "./ReviewPage";
import LeadOnboardingPage from "./LeadOnboardingPage";
import DiscoveryCallPage from "./DiscoveryCallPage";
import BookDiscoveryPage from "./BookDiscoveryPage";
import MeetingRequests from "./MeetingRequests";
import MeetingRequestPage from "./MeetingRequestPage";
import EpisodeSharePage from "./EpisodeSharePage";
import SetPasswordPage from "./SetPasswordPage";
import UserManagement from "./UserManagement";
import { getCurrentUser, logout as logoutApi } from "./api";
import { getThemePreference, applyThemePreference } from "./theme";

function App() {
    const [token, setToken] = useState(localStorage.getItem("token"));
    const [user, setUser] = useState(null);
    const [page, setPage] = useState("dashboard");

    // Initialise theme (system / light / dark) for the whole app
    useEffect(() => {
        try {
            const pref = getThemePreference ? getThemePreference() : "system";
            if (applyThemePreference) {
                applyThemePreference(pref);
            }
        } catch (err) {
            // If anything goes wrong, just fail silently – UI will still work
            console.error("Failed to apply theme preference:", err);
        }
    }, []);

    const handleLoginSuccess = (userData) => {
        setUser(userData);
        setToken(localStorage.getItem("token"));
    };

    const handleLogout = async ({ notifyServer = true } = {}) => {
        if (notifyServer && token) {
            try {
                await logoutApi();
            } catch (err) {
                // best-effort audit event; continue local logout
                console.error("Logout API call failed:", err);
            }
        }
        localStorage.removeItem("token");
        setUser(null);
        setToken(null);
    };

    useEffect(() => {
        let cancelled = false;

        async function hydrateUser() {
            if (!token) return;
            if (user) return;

            try {
                const res = await getCurrentUser();
                if (cancelled) return;

                if (!res || !res.ok || !res.user) {
                    handleLogout({ notifyServer: false });
                    return;
                }

                setUser(res.user);
            } catch (err) {
                console.error("Failed to hydrate current user:", err);
                if (!cancelled) {
                    handleLogout({ notifyServer: false });
                }
            }
        }

        hydrateUser();
        return () => {
            cancelled = true;
        };
    }, [token, user]);

    useEffect(() => {
        function onHashChange() {
            const hash = window.location.hash.replace("#/", "");
            setPage(hash || "dashboard");
        }

        window.addEventListener("hashchange", onHashChange);
        onHashChange();

        return () => window.removeEventListener("hashchange", onHashChange);
    }, []);

    // 🔓 PUBLIC MAGIC-LINK REVIEW ROUTE (NO LOGIN)
    // Link format: https://your-portal-url/#/review?token=ABC123
    if (page && page.startsWith("review")) {
        return <ReviewPage />;
    }

    if (page && page.startsWith("onboarding")) {
        return <LeadOnboardingPage />;
    }

    if (page && page.startsWith("discovery-call")) {
        return <DiscoveryCallPage />;
    }

    if (page && page.startsWith("book-discovery")) {
        return <BookDiscoveryPage />;
    }

    if (page && page.startsWith("meeting-request")) {
        return <MeetingRequestPage />;
    }

    if (page && page.startsWith("episode-share")) {
        return <EpisodeSharePage />;
    }

    if (page && page.startsWith("set-password")) {
        return <SetPasswordPage />;
    }

    // AUTH-ONLY PORTAL BELOW HERE
    if (!token) {
        return <Login onLoginSuccess={handleLoginSuccess} />;
    }

    if (!user) {
        return <div style={{ padding: 24 }}>Loading user…</div>;
    }

    const isClient = user.role === "client";
    let content = null;

    if (isClient) {
        if (page === "dashboard") {
            content = (
                <ClientDashboard
                    onLogout={handleLogout}
                    embedded
                />
            );
        } else if (page === "settings") {
            content = (
                <Settings
                    token={token}
                    onLogout={handleLogout}
                    embedded
                />
            );
        } else if (page === "files") {
            content = <FileExplorer onLogout={handleLogout} embedded />;
        } else if (page.startsWith("episodes")) {
            content = (
                <Episodes
                    token={token}
                    onLogout={handleLogout}
                    embedded
                />
            );
        } else {
            content = <ClientDashboard onLogout={handleLogout} embedded />;
        }
        return (
            <Layout onLogout={handleLogout} currentUser={user}>
                {content}
            </Layout>
        );
    }

    // ROUTING
    if (page === "settings") {
        content = <Settings token={token} onLogout={handleLogout} embedded />;
    } else if (page === "clients") {
        content = <Clients token={token} onLogout={handleLogout} embedded />;
    } else if (page === "leads") {
        content = <Leads token={token} onLogout={handleLogout} embedded />;
    } else if (page === "meetings") {
        content = <MeetingRequests onLogout={handleLogout} embedded />;
    } else if (page === "podcasts") {
        content = <Podcasts token={token} onLogout={handleLogout} embedded />;
    } else if (page === "files") {
        content = <FileExplorer onLogout={handleLogout} embedded />;
    } else if (page === "user-management") {
        content = (
            <UserManagement token={token} onLogout={handleLogout} embedded />
        );
    } else if (page.startsWith("episodes")) {
        content = <Episodes token={token} onLogout={handleLogout} embedded />;
    } else if (page === "dashboard") {
        content = <Dashboard token={token} onLogout={handleLogout} embedded />;
    } else {
        // Fallback
        content = <Dashboard token={token} onLogout={handleLogout} embedded />;
    }
    return (
        <Layout onLogout={handleLogout} currentUser={user}>
            {content}
        </Layout>
    );
}

export default App;
